// ============================================================
// BATTLEFIELD SHADING - flat light for flat solids.
//
// There is no lighting model here and there should not be one. The map
// is a diagram: faces need to be TELLABLE APART so a box reads as a box,
// and nothing more. A fixed key direction, a dot product, and a handful
// of quantised steps give the stylised look the rest of the app has,
// without a gradient in sight.
//
// Pure. Zero React dependency.
// ============================================================
import type { TerrainKind, Vec3 } from './types';

/**
 * Which way the light comes from, as an angle in the ground plane.
 *
 * Deliberately NOT a multiple of 45°: on that alignment the four sides of
 * a grid-aligned box fall into two pairs of equal brightness once the
 * result is quantised, and the box reads as a flat hexagon. Off-axis,
 * every face of a box gets its own step.
 */
const KEY_AZIMUTH = 0.6; // radians

/** How many distinct brightnesses a side face can take. */
const STEPS = 6;
/** Even an unlit face is visible - this is a diagram, not a cave. */
const FLOOR = 0.3;
/** Sides live strictly between the underside and the top. */
const SIDE_MIN = 0.4;
const SIDE_MAX = 0.85;
/** Steeper than this and a face is a cap, not a wall. */
const CAP_THRESHOLD = 0.9;

export function shadeSteps(): number {
  return STEPS;
}

/**
 * Brightness in [0, 1] for a face with the given outward normal.
 *
 * Caps are fixed points - a top is always the brightest surface and an
 * underside always the darkest - and everything else is shaded by which
 * way it FACES, quantised into steps. Ordering by role rather than by a
 * single dot product is what guarantees the property the map depends on:
 * a solid's top always reads as its top, at every orientation.
 */
export function faceShade(normal: Vec3): number {
  const len = Math.hypot(normal.x, normal.y, normal.z);
  if (!(len > 1e-9)) return FLOOR;
  const ny = normal.y / len;
  if (ny >= CAP_THRESHOLD) return 1;
  if (ny <= -CAP_THRESHOLD) return FLOOR;

  const azimuth = Math.atan2(normal.z, normal.x);
  const facing = Math.cos(azimuth - KEY_AZIMUTH); // 1 = square into the light
  const band = Math.round(((facing + 1) / 2) * (STEPS - 1));
  return SIDE_MIN + ((SIDE_MAX - SIDE_MIN) * band) / (STEPS - 1);
}

/**
 * A colour wash announcing what a piece of terrain DOES, laid over the
 * shade. Normal ground says nothing - the map would be unreadable if
 * every surface shouted.
 */
export const TERRAIN_TINTS: Record<TerrainKind, string | null> = {
  normal: null,
  difficult: '#a16207', // slow going - mud, rubble, scrap
  dangerous: '#b91c1c', // it can hurt you
  impassable: '#1f2937', // it simply stops you
};

export function terrainTint(kind: TerrainKind): string | null {
  return TERRAIN_TINTS[kind] ?? null;
}
